import { autoUpdate, flip, shift, useFloating } from '@floating-ui/react-dom'
import { PageLocaleEnum, PageLocaleNames } from 'common/enums/PageLocaleEnum'
import * as urlUtils from 'common/utils/urlUtils'
import React from 'react'
import { useFloatHover } from '../../../hooks/useFloatHover'
import { LanguageSwitcherInterface } from '../../../types/entities/LanguageSwitcherInterface'
import {
  LanguageSwitcherCurrentLangArrowUi,
  LanguageSwitcherCurrentLangUi,
} from './ui/LanguageSwitcherCurrentLangUi'
import LanguageSwitcherItemsLinkUi from './ui/LanguageSwitcherItemsLinkUi'
import LanguageSwitcherItemsUi from './ui/LanguageSwitcherItemsUi'
import LanguageSwitcherUi from './ui/LanguageSwitcherUi'

export const localeKeys = {
  en: 'en',
  fr: 'fr',
  es: 'es',
  it: 'it',
  pt: 'pt',
  de: 'de',
  nl: 'nl',
  ru: 'ru',
  jp: 'jp', // why not ja? @see https://systeme-team.slack.com/archives/G61JS7GDC/p1684234537974629
  ar: 'ar',
  tr: 'tr',
  zh: 'zh',
  sv: 'sv',
  ro: 'ro',
  cs: 'cs',
  hu: 'hu',
  sk: 'sk',
  dk: 'dk',
  id: 'id',
  pl: 'pl',
  el: 'el',
  sr: 'sr',
  no: 'no',
  th: 'th',
  sl: 'sl',
  ua: 'ua',
  sq: 'sq',
  hi: 'hi',
}

// export const localeNames = {
//   [localeKeys.en]: 'English',
//   [localeKeys.fr]: 'Français',
//   [localeKeys.es]: 'Español',
//   [localeKeys.it]: 'Italiano',
//   [localeKeys.pt]: 'Português',
//   [localeKeys.de]: 'Deutsch',
//   [localeKeys.nl]: 'Nederlands',
//   [localeKeys.ru]: 'Русский',
//   [localeKeys.jp]: '日本語',
//   [localeKeys.ar]: 'عربي',
//   [localeKeys.tr]: 'Türkçe',
//   [localeKeys.zh]: '中國人',
//   [localeKeys.sv]: 'Svenska',
//   [localeKeys.ru]: 'Română',
//   [localeKeys.cs]: 'Čeština',
//   [localeKeys.hu]: 'Magyar',
//   [localeKeys.sk]: 'Slovenský',
//   [localeKeys.da]: 'Dansk',
//   [localeKeys.id]: 'Indonesian',
//   [localeKeys.pl]: 'Polski',
//   [localeKeys.el]: 'Ελληνικά',
//   [localeKeys.sr]: 'Srpski',
//   [localeKeys.hi]: 'Hindi',
//   [localeKeys.no]: 'Norsk',
//   [localeKeys.th]: 'ไทย',
//   [localeKeys.sl]: 'Slovenič',
//   [localeKeys.uk]: 'Український',
//   [localeKeys.sq]: 'Shqiptare',
// }

interface LanguageSwitcherProps
  extends Omit<
    LanguageSwitcherInterface,
    'id' | 'type' | 'parentId' | 'appearance' | 'htmlAttrId'
  > {
  currentLanguage: string
  isEditing?: boolean
}

function NewLanguageSwitcher({
  color,
  margin,
  fontSize,
  fontStyle,
  fontFamily,
  fontWeight,
  lineHeight,
  mobileFontSize,
  mobileLineHeight,
  mobileFontStyle,
  mobileFontFamily,
  mobileFontWeight,
  mobileMargin,
  mobileColor,
  currentLanguage,
  redirectPath,
  excludedLanguages,
}: LanguageSwitcherProps) {
  const { show, delayOpen, delayClose } = useFloatHover()

  const { refs, floatingStyles } = useFloating<HTMLDivElement>({
    placement: 'bottom',
    middleware: [flip(), shift()],
    whileElementsMounted: autoUpdate,
    open: show,
    strategy: 'absolute',
  })

  const getUrl = (locale: string): string => {
    const normalizedLocale = locale === PageLocaleEnum.TURKISH ? 'tur' : locale

    if (redirectPath === undefined) {
      return normalizedLocale === PageLocaleEnum.ENGLISH
        ? `/`
        : `/${normalizedLocale}`
    }

    const clearRedirectPath =
      urlUtils.trimLeadingAndTrailingSlashes(redirectPath)

    return normalizedLocale === PageLocaleEnum.ENGLISH
      ? `/${clearRedirectPath}`
      : `/${normalizedLocale}/${clearRedirectPath}`
  }

  return (
    <LanguageSwitcherUi margin={margin} mobileMargin={mobileMargin}>
      <LanguageSwitcherCurrentLangUi
        fontSize={fontSize}
        color={color}
        fontFamily={fontFamily}
        fontWeight={fontWeight}
        fontStyle={fontStyle}
        lineHeight={lineHeight}
        mobileColor={mobileColor}
        mobileFontSize={mobileFontSize}
        mobileFontFamily={mobileFontFamily}
        mobileFontWeight={mobileFontWeight}
        mobileFontStyle={mobileFontStyle}
        mobileLineHeight={mobileLineHeight}
        onMouseEnter={delayOpen}
        onMouseLeave={delayClose}
        ref={refs.setReference}
      >
        {currentLanguage.charAt(0).toUpperCase() + currentLanguage.slice(1)}
        <LanguageSwitcherCurrentLangArrowUi>
          ⌄
        </LanguageSwitcherCurrentLangArrowUi>
      </LanguageSwitcherCurrentLangUi>
      {show && (
        <LanguageSwitcherItemsUi
          ref={refs.setFloating}
          onMouseEnter={delayOpen}
          onMouseLeave={delayClose}
          isOpened={show}
          style={floatingStyles}
        >
          {Object.keys(PageLocaleNames)
            .filter(countryKey => countryKey !== currentLanguage)
            .filter(countryKey => !excludedLanguages?.includes(countryKey))
            .map(countryKey => (
              <LanguageSwitcherItemsLinkUi
                key={countryKey}
                href={getUrl(countryKey)}
              >
                {PageLocaleNames[countryKey as PageLocaleEnum]}
              </LanguageSwitcherItemsLinkUi>
            ))}
        </LanguageSwitcherItemsUi>
      )}
    </LanguageSwitcherUi>
  )
}

export default NewLanguageSwitcher
